// ─────────────────────────────────────────────
//  ProviderPage — "My Room" view (CRNA/AA and supervising doctors)
//
//  Shows the room(s) the logged-in user is on today, rendered with the SAME
//  RoomCard as the OR board. A supervising doctor sees every room they cover;
//  a CRNA/AA sees their assigned room(s). Matching is role-aware and bridges the
//  uid↔staffId gap (see domain/myRooms) — this is the fix for "Not assigned yet"
//  showing even when rooms were assigned. The CRNA self-service actions (break,
//  status, sign out) stay below, available once signed into a live room.
// ─────────────────────────────────────────────
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import {
  signOutOfRoom,
  listenToMyBreaks,
  setRoomCondition, clearRoomCondition,
  setRoomFollowUp, clearRoomFollowUp, markFollowUpArrived,
} from "../firebase/firestore";
import { BREAK_CHANNELS } from "../domain/breaks";
import {
  makeCondition, makeFollowUpPair, makeHandoffEntry,
} from "../domain/conditions";
import { resolveRoomDoctorId } from "../domain/alerts";
import ConditionPicker from "../components/ConditionPicker";
import RoomChainTrail from "../components/RoomChainTrail";
import { useDailyRoster } from "../hooks/useDailyRoster";
import { useDayKey } from "../hooks/useDayKey";
import { useMyRooms } from "../hooks/useMyRooms";
import { useLocations } from "../hooks/useLocations";
import { useFacility } from "../hooks/useFacility";
import { resolveFacilityRules } from "../domain/facilityRules";
import { isDoctorViewer } from "../domain/myRooms";
import { Modal, Button, RoomCard, BoardAlertControls, SignOutButton } from "../components";
import NavMenu from "../components/NavMenu";
import ChannelList from "../components/ChannelList";
import ConversationView from "../components/ConversationView";
import UnreadBadge from "../components/UnreadBadge";
import { useMessaging } from "../context/MessagingContext";
import { isSandboxBoot } from "../sandbox/mode";
import { useAlertCenter } from "../context/AlertContext";
import { useIsMobile } from "../hooks/useIsMobile";
import { unlockAudioOnFirstGesture } from "../audio/chime";
import tokens from "../theme/tokens";

const { color, space, radius, shadow, font } = tokens;

export default function ProviderPage() {
  const { user } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const {
    urgencyForRoom, raiseBreakRequest,
    myConditionAlerts, raiseRoomCondition, resolveConditionAlertsForRoom, resolve: resolveAlertById,
  } = useAlertCenter();
  const facilityId = user?.activeFacilityId;

  // Canonical domain channel (lowercase) — the SAME value the board card
  // renders, so the requested type always matches the displayed type.
  const [breakType, setBreakType]   = useState("lunch");
  const [showBreak, setShowBreak]   = useState(false);
  const [showSignOut, setShowSignOut] = useState(false);
  const [busy, setBusy]             = useState(false);
  // Phase 5.2 — room condition + provider handoff.
  const [showCondition, setShowCondition] = useState(false);
  const [showFollowUp, setShowFollowUp]   = useState(false);
  const [followDestId, setFollowDestId]   = useState("");

  // ── Inline messaging (replaces the floating launcher) ──
  // The "Message Doctor" card expands the chat surface in place on this page:
  // ChannelList (pick a room/group/DM from today's derived channels) + the
  // ConversationView thread/composer. selection = ChannelList's picked item.
  const [showMessaging, setShowMessaging] = useState(false);
  const [selection, setSelection] = useState(null);
  const { unread } = useMessaging();
  const isNarrow = useIsMobile(640);

  const toggleMessaging = () => {
    // First gesture unlocks audio so an incoming-message chime can play (D-03).
    unlockAudioOnFirstGesture();
    setShowMessaging((open) => !open);
  };

  const hasRoom = !!user?.currentRoomId;
  const isDoctor = isDoctorViewer(user);

  const { locations } = useLocations(facilityId);
  const dateKey = useDayKey(); // LOCAL day, reactive across midnight
  const { roster } = useDailyRoster(facilityId, dateKey);
  // Phase 5.4 — per-facility rules (room-condition escalation).
  const { facility } = useFacility(facilityId);
  const facilityRules = resolveFacilityRules(facility);

  // Live data + derivations for the room cards — the shared "rooms I'm on
  // today" pipeline (extracted to useMyRooms so the mobile dashboards render
  // from the exact same derivation). Staff pools ride along from the hook so
  // this page keeps a single staff listener.
  const {
    myCards, coverCards, myRoom, myAssignment, myDoctorId,
    rooms, assignments, awaitingRoom, followingRoom, chainSteps,
    doctors, providers,
  } = useMyRooms(facilityId, dateKey);

  // Doctors enriched with today's call order, for the cards' supervisor line.
  const doctorsWithCall = useMemo(() => {
    const callById = new Map((roster?.supervisors || []).map((s) => [s.id, s.callOrder]));
    return (doctors || []).map((d) => (callById.has(d.id) ? { ...d, callOrder: callById.get(d.id) } : d));
  }, [doctors, roster]);

  const myDoctorName =
    myAssignment?.attendingName || myRoom?.supervisorName || user?.supervisorName || "your doctor";

  const handlePostCondition = async ({ kind, note }) => {
    if (!myRoom) { toast.error("You're not signed into a room yet. Ask the AIC to assign you."); return; }
    setBusy(true);
    try {
      const condition = makeCondition({ kind, note, setById: user?.uid, setByName: user?.name || "" });
      await setRoomCondition(myRoom.id, condition);
      // Quiet ping to the supervising doctor (no alarm — informational).
      if (myDoctorId) {
        try {
          await raiseRoomCondition({
            roomId: myRoom.id, roomName: myRoom.name, doctorId: myDoctorId, kind,
            // Phase 5.4 rules: optionally escalate to the day's AIC as well.
            extraTargetIds: facilityRules.notifyAicOnCondition && roster?.aicId ? [roster.aicId] : [],
            message: `🩺 ${myRoom.name}: ${kind.replace(/_/g, " ")}${note ? ` — ${note.slice(0, 80)}` : ""} (${user?.name || "provider"})`,
          });
        } catch (e) { console.error("Condition notify failed:", e); }
      }
      setShowCondition(false);
      toast.success(`Condition posted — ${myDoctorName} notified.`);
    } catch (err) {
      console.error("Post condition failed:", err);
      toast.error("Could not update the condition. Check your connection and try again.");
    } finally {
      setBusy(false);
    }
  };

  const handleClearCondition = async () => {
    if (!myRoom) return;
    setBusy(true);
    try {
      await clearRoomCondition(myRoom.id);
      try { await resolveConditionAlertsForRoom(myRoom.id); } catch (e) { console.error(e); }
      setShowCondition(false);
      toast.success(`Condition cleared for ${myRoom.name}.`);
    } catch (err) {
      console.error("Clear condition failed:", err);
      toast.error("Could not update the condition. Check your connection and try again.");
    } finally {
      setBusy(false);
    }
  };

  const handleSetFollowUp = async () => {
    const dest = rooms.find((r) => r.id === followDestId);
    if (!myRoom || !dest) return;
    setBusy(true);
    try {
      const pair = makeFollowUpPair({
        providerId: user?.uid,
        providerName: user?.name || "",
        fromRoomId: myRoom.id,
        fromRoomName: myRoom.name,
        toRoomId: dest.id,
        toRoomName: dest.name,
      });
      await setRoomFollowUp(myRoom.id, dest.id, pair);
      const destDoctorId = resolveRoomDoctorId(dest, assignments.find((a) => a.orRoom === dest.name));
      if (destDoctorId || myDoctorId) {
        try {
          await raiseRoomCondition({
            roomId: dest.id, roomName: dest.name, doctorId: destDoctorId || myDoctorId, kind: "handoff",
            message: `🔁 ${user?.name || "Provider"} following ${myRoom.name} → ${dest.name}`,
          });
        } catch (e) { console.error("Follow-up notify failed:", e); }
      }
      setShowFollowUp(false);
      setFollowDestId("");
      toast.success(`Following to ${dest.name} — ${myDoctorName} notified.`);
    } catch (err) {
      console.error("Set follow-up failed:", err);
      toast.error("Could not update the handoff. Check your connection and try again.");
    } finally {
      setBusy(false);
    }
  };

  const handleCancelFollowUp = async () => {
    if (!followingRoom) return;
    setBusy(true);
    try {
      await clearRoomFollowUp(followingRoom.id, followingRoom.followUp?.toRoomId || null);
      toast.info("Follow-up cancelled.");
    } catch (err) {
      console.error("Cancel follow-up failed:", err);
      toast.error("Could not update the handoff. Check your connection and try again.");
    } finally {
      setBusy(false);
    }
  };

  const handleMarkArrived = async () => {
    if (!awaitingRoom) return;
    setBusy(true);
    try {
      const entry = makeHandoffEntry({
        followUp: awaitingRoom.followUp,
        toRoomId: awaitingRoom.id,
        toRoomName: awaitingRoom.name,
        dateKey,
      });
      await markFollowUpArrived(awaitingRoom.followUp?.fromRoomId || null, awaitingRoom.id, entry);
      const destDoctorId = resolveRoomDoctorId(
        awaitingRoom,
        assignments.find((a) => a.orRoom === awaitingRoom.name)
      );
      if (destDoctorId) {
        try {
          await raiseRoomCondition({
            roomId: awaitingRoom.id, roomName: awaitingRoom.name, doctorId: destDoctorId, kind: "arrived",
            message: `✅ ${user?.name || "Provider"} arrived in ${awaitingRoom.name}`,
          });
        } catch (e) { console.error("Arrival notify failed:", e); }
      }
      toast.success(`Arrived in ${awaitingRoom.name}. ${myDoctorName} notified.`);
    } catch (err) {
      console.error("Mark arrived failed:", err);
      toast.error("Could not update the handoff. Check your connection and try again.");
    } finally {
      setBusy(false);
    }
  };

  // ── Phase 5.3: my break request status (requested / approved / denied) ──
  const [myBreaks, setMyBreaks] = useState([]);
  const [dismissedBreakIds, setDismissedBreakIds] = useState(() => new Set());
  useEffect(() => {
    if (!facilityId || !user?.uid || isDoctor) return undefined;
    return listenToMyBreaks(facilityId, user.uid, (snap) => {
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setMyBreaks(rows.filter((b) => !b.date || b.date === dateKey));
    }, (err) => console.error("[ProviderPage] my-breaks listener failed:", err));
  }, [facilityId, user?.uid, dateKey, isDoctor]);
  // Latest actionable status rows: pending requests + today's decided ones the
  // user hasn't dismissed.
  const myBreakStatus = useMemo(
    () =>
      myBreaks
        .filter((b) => ["requested", "approved", "denied"].includes(b.status))
        .filter((b) => !dismissedBreakIds.has(b.id))
        .sort((a, b) => String(b.requestedAt?.seconds || "").localeCompare(String(a.requestedAt?.seconds || ""))),
    [myBreaks, dismissedBreakIds]
  );

  // Doctor-side: clear a condition straight from the notification strip (either
  // party may clear — SC2). Falls back to just dismissing the ping.
  const handleDoctorClear = async (alert) => {
    setBusy(true);
    try {
      const room = rooms.find((r) => r.id === alert.roomId);
      if (room?.condition) await clearRoomCondition(room.id);
      if (alert.roomId) await resolveConditionAlertsForRoom(alert.roomId);
      else await resolveAlertById(alert.id);
      toast.success(`Condition cleared for ${alert.roomName || "room"}.`);
    } catch (err) {
      console.error("Doctor clear condition failed:", err);
      toast.error("Could not update the condition. Check your connection and try again.");
    } finally {
      setBusy(false);
    }
  };

  const requireRoom = () => {
    if (!hasRoom) {
      toast.error("You're not signed into a room yet. Ask the AIC to assign you.");
      return false;
    }
    return true;
  };

  const handleBreakRequest = async () => {
    // Prefer the live assignment-derived room (5.2 myRoom) so a rostered CRNA
    // can ask even before the legacy currentRoomId is stamped on their user doc.
    const roomId = myRoom?.id || user?.currentRoomId || null;
    const roomName = myRoom?.name || user?.currentRoomName || "My Room";
    if (!roomId) {
      toast.error("You're not signed into a room yet. Ask the AIC to assign you.");
      return;
    }
    setBusy(true);
    try {
      // No approval step: the request goes straight onto every board as the
      // yellow break-request card, and any free CRNA/doctor covers it. The
      // /breaks record is written by whoever takes the cover (RoomCard).
      const res = await raiseBreakRequest({
        roomId,
        roomName,
        doctorId: myDoctorId,
        giverId: user.uid,
        giverName: user.name,
        breakType,
      });
      if (res?.reason === "duplicate") {
        toast.info(`A break is already requested for ${roomName}.`);
      } else if (res && !res.ok) {
        throw new Error("Break-request alert failed");
      } else {
        toast.success("Break request is on the board — a free CRNA or doctor can cover you.");
      }
      setShowBreak(false);
    } catch (err) {
      console.error("Break request failed:", err);
      toast.error("Could not send the break request. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const handleSignOut = async () => {
    if (!requireRoom()) { setShowSignOut(false); return; }
    setBusy(true);
    try {
      await signOutOfRoom(user.currentRoomId);
      toast.success("Signed out of your room.");
      setShowSignOut(false);
      navigate("/board");
    } catch (err) {
      console.error("Sign out failed:", err);
      toast.error("Could not sign out of the room.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={s.page}>
      <div style={s.topbar}>
        <div style={{ display: "flex", gap: 12, alignItems: "center", minWidth: 0 }}>
          <span style={s.logo}>⚕ ATC</span>
          <NavMenu />
        </div>
        {/* Emergency + Urgency for the provider's own room(s). Compact (small
            icon buttons) for CRNAs/AAs/residents and on phones; full size for
            doctors on the web. */}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <BoardAlertControls myRooms={myCards.map((c) => c.room)} rooms={myCards.map((c) => c.room)} assignments={assignments} compact={isNarrow || !isDoctor} directSingle={!isDoctor} />
          {/* ⚠️ This leaves the ACCOUNT. The "Sign Out of Room" panel below
              releases the room and keeps you logged in — two consequences, so
              only one of them may be called plain "Sign out". */}
          <SignOutButton compact={isNarrow} />
        </div>
      </div>

      <div style={s.content}>
        <div style={s.headRow}>
          <div style={s.h1}>{isDoctor ? "My Rooms" : "My Room"}</div>
          {myCards.length > 0 && (
            <span style={s.countPill}>{myCards.length} room{myCards.length === 1 ? "" : "s"}</span>
          )}
        </div>

        {/* Phase 5.2 — quiet room notifications for the supervising doctor:
            condition posts + handoff events targeting me. No alarm, no blink. */}
        {isDoctor && myConditionAlerts.length > 0 && (
          <div style={s.panel} aria-live="polite">
            <div style={s.panelTitle}>🩺 Room notifications ({myConditionAlerts.length})</div>
            {myConditionAlerts.map((a) => (
              <div key={a.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0", borderTop: `1px solid ${color.border.default}` }}>
                <span style={{ flex: 1, fontSize: 13, color: color.text.primary }}>{a.message || `${a.roomName || "Room"} update`}</span>
                <Button variant="secondary" onClick={() => handleDoctorClear(a)} disabled={busy}>
                  Clear
                </Button>
              </div>
            ))}
          </div>
        )}

        {/* Room cards live INSIDE this box; an empty box shows the centered
            "Not assigned yet" message. All cards stretch to the same height. */}
        <div style={s.roomBox}>
          {myCards.length > 0 ? (
            <div style={s.cardGrid}>
              {myCards.map(({ key, room, assignment }) => (
                <div key={key} style={s.cardWrap}>
                  <RoomCard
                    room={room}
                    assignment={assignment}
                    canEdit
                    canManage={isDoctor}
                    locations={locations}
                    doctors={doctorsWithCall}
                    rosterCrnas={providers}
                    rosterDoctors={doctors}
                    urgency={urgencyForRoom(room.id)}
                    rooms={rooms}
                    allAssignments={assignments}
                  />
                </div>
              ))}
            </div>
          ) : (
            <div style={s.emptyInner} role="status">
              <div style={s.emptyTitle}>Not assigned yet</div>
              <div style={s.emptyBody}>
                {isDoctor
                  ? "You're not supervising any rooms today."
                  : "Ask the AIC to assign you to a room."}
              </div>
            </div>
          )}
        </div>

        {/* Breaks needing coverage (Prompt 9) — rooms elsewhere blinking yellow
            for a break. Any free provider can take one over here. */}
        {coverCards.length > 0 && (
          <div style={{ marginTop: 18 }}>
            <div style={s.sectionLabel}>☕ Breaks needing coverage ({coverCards.length})</div>
            <div style={s.cardGrid}>
              {coverCards.map(({ key, room, assignment }) => (
                <div key={key} style={s.cardWrap}>
                  <RoomCard
                    room={room}
                    assignment={assignment}
                    canEdit={false}
                    canManage={false}
                    locations={locations}
                    doctors={doctorsWithCall}
                    rosterCrnas={providers}
                    rosterDoctors={doctors}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Phase 5.3 — my break request status: waiting / approved with named
            coverage / denied with the doctor's reason. */}
        {!isDoctor && myBreakStatus.length > 0 && (
          <div style={s.panel} aria-live="polite">
            <div style={s.panelTitle}>☕ My break request</div>
            {myBreakStatus.map((b) => (
              <div key={b.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0", borderTop: `1px solid ${color.border.default}`, flexWrap: "wrap" }}>
                <span style={{ flex: 1, minWidth: 180, fontSize: 13, color: color.text.primary }}>
                  {b.type} · {b.roomName}{" — "}
                  {b.status === "requested" && <span style={{ color: "#7a4f00", fontWeight: 700 }}>waiting for approval…</span>}
                  {b.status === "approved" && (
                    <span style={{ color: "#096e53", fontWeight: 700 }}>
                      approved — {b.receiverName || "coverage"} covers{b.estimatedMinutes ? ` (${b.estimatedMinutes} min)` : ""}
                    </span>
                  )}
                  {b.status === "denied" && (
                    <span style={{ color: color.accent.error, fontWeight: 700 }}>
                      denied{b.denyReason ? ` — "${b.denyReason}"` : ""}
                    </span>
                  )}
                </span>
                {b.status !== "requested" && (
                  <Button variant="secondary" onClick={() => setDismissedBreakIds((prev) => new Set([...prev, b.id]))}>
                    Dismiss
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Provider handoff (Phase 5.2): follow-up chain controls + the room
            chain trail for the shift. Not shown for doctors (they see the
            banners on their room cards + the notification strip). */}
        {!isDoctor && (chainSteps.length >= 2 || awaitingRoom || followingRoom || myRoom) && (
          <div style={s.panel}>
            <div style={s.panelTitle}>🔁 Provider Handoff</div>
            {chainSteps.length >= 2 && (
              <div style={{ marginBottom: 10 }}>
                <RoomChainTrail steps={chainSteps} />
              </div>
            )}
            {awaitingRoom ? (
              <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                <span style={{ fontSize: 13, color: color.text.primary }}>
                  ⏳ {awaitingRoom.name} is awaiting your arrival
                  {awaitingRoom.followUp?.fromRoomName ? ` from ${awaitingRoom.followUp.fromRoomName}` : ""}.
                </span>
                <Button onClick={handleMarkArrived} disabled={busy}>
                  {busy ? "Saving…" : "Mark Arrived"}
                </Button>
              </div>
            ) : followingRoom ? (
              <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                <span style={{ fontSize: 13, color: color.text.primary }}>
                  → Following to {followingRoom.followUp?.toRoomName || "another room"}.
                </span>
                <Button variant="secondary" onClick={handleCancelFollowUp} disabled={busy}>
                  Cancel Follow-Up
                </Button>
              </div>
            ) : myRoom ? (
              <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                <span style={{ fontSize: 13, color: color.text.muted }}>Not following to another room.</span>
                <Button variant="secondary" onClick={() => setShowFollowUp(true)} disabled={busy}>
                  Set Follow-Up
                </Button>
              </div>
            ) : null}
          </div>
        )}

        {/* Self-service actions — operate on the room you're signed into. On a
            phone these stack full-width for big, readable tap targets. */}
        <div style={{ ...s.actionGrid, ...(isNarrow ? { gridTemplateColumns: "1fr" } : {}), ...(!isDoctor && !isNarrow ? { gridTemplateColumns: "1fr 1fr 1fr" } : {}) }}>
          {/* Phase 5.3: gate on the live assignment-derived room (myRoom), not
              only the legacy currentRoomId — a rostered CRNA can ask for a
              break as soon as the grid assigns them. */}
          <button style={{ ...s.actionCard, borderColor: "#f97316", ...((hasRoom || myRoom) ? {} : s.actionDisabled) }}
            onClick={() => setShowBreak(true)} disabled={!(hasRoom || myRoom)}>
            <div style={s.actionIcon} aria-hidden="true">☕</div>
            <div style={s.actionTitle}>Request Break</div>
            <div style={s.actionSub}>Post it on the board — a free CRNA or doctor covers you.</div>
          </button>
          {!isDoctor && (
            <button
              style={{ ...s.actionCard, borderColor: "#a855f7", ...(myRoom ? {} : s.actionDisabled) }}
              onClick={() => setShowCondition(true)}
              disabled={!myRoom}
            >
              <div style={s.actionIcon} aria-hidden="true">🩺</div>
              <div style={s.actionTitle}>Room Condition</div>
              <div style={s.actionSub}>
                {myRoom?.condition?.kind
                  ? "Update or clear the posted condition."
                  : `Let ${myDoctorName} know what's happening in your room.`}
              </div>
            </button>
          )}
          {/* Hidden in the sandbox, which has exactly one user and promises no
              inter-user communication. This is the SECOND messaging entry point
              in the app — MessagingLauncher is the other — and guarding only
              that one would leave this card sitting here opening a permanently
              empty inbox, which reads as a broken feature rather than a
              smaller app. */}
          {!isSandboxBoot() && (
          <button
            style={{ ...s.actionCard, ...(showMessaging ? s.actionActive : {}) }}
            onClick={toggleMessaging}
            aria-expanded={showMessaging}
            aria-controls="provider-messaging"
          >
            <div style={s.actionIconRow}>
              <span style={s.actionIcon} aria-hidden="true">💬</span>
              <UnreadBadge count={Number.isFinite(unread) ? unread : 0} />
            </div>
            <div style={s.actionTitle}>Messages</div>
            <div style={s.actionSub}>
              {showMessaging ? "Close the chat panel." : "Message your team and supervising doctor by room."}
            </div>
          </button>
          )}
        </div>

        {/* Inline chat surface — expands in place when "Messages" is toggled on.
            Reuses the Phase-5 ChannelList (today's derived rooms/groups/DMs) +
            ConversationView (thread + composer). Master/detail side-by-side on
            desktop; on narrow screens the list swaps to the conversation. */}
        {showMessaging && !isSandboxBoot() && (
          <section id="provider-messaging" style={s.msgPanel} aria-label="Messages">
            <div style={s.msgHeader}>
              <div style={s.msgTitle}>💬 Messages</div>
              <button
                type="button"
                style={s.msgClose}
                onClick={() => setShowMessaging(false)}
                aria-label="Close messages"
              >
                ✕
              </button>
            </div>
            <div style={s.msgBody}>
              {(!isNarrow || !selection) && (
                <div style={{ ...s.msgMaster, ...(isNarrow ? s.msgMasterNarrow : {}) }}>
                  <ChannelList selectedId={selection?.id} onSelect={setSelection} />
                </div>
              )}
              {(!isNarrow || !!selection) && (
                <div style={s.msgDetail}>
                  <ConversationView
                    selection={selection}
                    onBack={isNarrow ? () => setSelection(null) : undefined}
                  />
                </div>
              )}
            </div>
          </section>
        )}

        <div style={s.panel}>
          <div style={s.panelTitle}>🚪 Sign Out of Room</div>
          <p style={{ fontSize: 13, color: color.text.muted, marginBottom: 14 }}>
            Only sign out when your case is complete and handed off.
          </p>
          <button style={{ ...s.outlineBtn, ...(hasRoom ? {} : s.actionDisabled) }}
            onClick={() => setShowSignOut(true)} disabled={!hasRoom}>
            Sign Out of Room
          </button>
        </div>
      </div>

      {showBreak && (
        <div style={s.overlay}>
          <div style={s.modal}>
            <div style={s.modalTitle}>☕ Request Break</div>
            <div style={s.modalSub}>Goes straight to the board — free CRNAs and doctors can cover you.</div>
            <div style={s.formGroup}>
              <label style={s.label} htmlFor="provider-break-type">Break Type</label>
              <select id="provider-break-type" style={s.select} value={breakType} onChange={(e) => setBreakType(e.target.value)}>
                {BREAK_CHANNELS.map((ch) => (
                  <option key={ch} value={ch}>{ch.charAt(0).toUpperCase() + ch.slice(1)}</option>
                ))}
              </select>
            </div>
            <div style={s.modalActions}>
              <button style={s.outlineBtn} onClick={() => setShowBreak(false)} disabled={busy}>Cancel</button>
              <button style={s.warningBtn} onClick={handleBreakRequest} disabled={busy}>{busy ? "Sending…" : "Submit Request"}</button>
            </div>
          </div>
        </div>
      )}

      {/* "Update Room Status" card + modal removed (2026-07-05, user request):
          room status changes live on the room cards' quick-action buttons. */}

      {/* Phase 5.2 — condition picker (shared Modal: focus trap + Escape). */}
      <ConditionPicker
        isOpen={showCondition}
        onClose={() => setShowCondition(false)}
        roomName={myRoom?.name || ""}
        condition={myRoom?.condition || null}
        busy={busy}
        onPost={handlePostCondition}
        onClear={handleClearCondition}
      />

      {/* Phase 5.2 — set follow-up destination. */}
      <Modal
        isOpen={showFollowUp}
        onClose={() => setShowFollowUp(false)}
        title="Provider Handoff"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowFollowUp(false)} disabled={busy}>
              Cancel
            </Button>
            <Button onClick={handleSetFollowUp} disabled={busy || !followDestId}>
              {busy ? "Saving…" : "Set Follow-Up"}
            </Button>
          </>
        }
      >
        <div style={{ fontSize: 13, color: color.text.muted, marginBottom: 14 }}>
          Your current room will show "Following to…" and the destination will show an
          "Awaiting arrival" standby look until you mark yourself arrived.
        </div>
        <label style={s.label} htmlFor="followup-dest">Follow to room</label>
        <select
          id="followup-dest"
          style={s.select}
          value={followDestId}
          onChange={(e) => setFollowDestId(e.target.value)}
        >
          <option value="">— choose a room —</option>
          {rooms
            .filter((r) => r.id !== myRoom?.id)
            .map((r) => (
              <option key={r.id} value={r.id}>{r.name}</option>
            ))}
        </select>
      </Modal>

      <Modal
        isOpen={showSignOut}
        onClose={() => setShowSignOut(false)}
        title="Sign out of this room?"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowSignOut(false)} disabled={busy}>Cancel</Button>
            <Button variant="danger" onClick={handleSignOut} disabled={busy}>{busy ? "Signing out…" : "Sign out of room"}</Button>
          </>
        }
      >
        {/* This copy used to read "Sign out?" / "You'll need to sign in again",
            which describes the ACCOUNT sign-out — a different action that now
            sits in this page's own topbar. Name the consequence instead. */}
        You'll come off the room on the board. You stay signed in.
      </Modal>
    </div>
  );
}

const s = {
  page:         { background: color.surface.base, color: color.text.primary, minHeight: "100vh", fontFamily: font.family },
  topbar:       { background: color.surface.card, borderBottom: `1px solid ${color.border.default}`, padding: "0 20px", height: 56, display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 10 },
  logo:         { fontSize: font.size.heading, fontWeight: font.weight.bold, color: color.accent.primary },
  backBtn:      { background: "transparent", border: "none", color: color.text.muted, cursor: "pointer", fontSize: 13 },
  content:      { maxWidth: 900, margin: "0 auto", padding: 24 },
  headRow:      { display: "flex", alignItems: "center", gap: 10, marginBottom: 16 },
  h1:           { fontSize: font.size.display, fontWeight: font.weight.bold, color: color.text.primary },
  countPill:    { background: color.surface.raised, border: `1px solid ${color.border.default}`, color: color.text.muted, borderRadius: radius.full, padding: "2px 10px", fontSize: font.size.label, fontWeight: font.weight.bold },
  sectionLabel: { fontSize: font.size.label, fontWeight: font.weight.bold, color: "#7a4f00", marginBottom: 8, textTransform: "uppercase", letterSpacing: ".4px" },
  // White container that holds the room cards (or the centered empty message).
  roomBox:      { background: color.surface.card, border: `1px solid ${color.border.default}`, boxShadow: shadow.sm, borderRadius: radius.lg, padding: 20, marginBottom: 20, minHeight: 120 },
  // alignItems:stretch → every card in a row is the same height.
  cardGrid:     { display: "flex", flexWrap: "wrap", gap: 14, alignItems: "stretch", justifyContent: "center" },
  cardWrap:     { width: 240, display: "flex" },
  emptyInner:   { textAlign: "center", padding: "28px 12px" },
  emptyTitle:   { fontSize: font.size.heading, fontWeight: font.weight.bold, color: color.text.primary, marginBottom: 6 },
  emptyBody:    { fontSize: font.size.body, color: color.text.muted },
  actionGrid:   { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14, marginBottom: 20 },
  actionCard:   { background: color.surface.card, border: `1px solid ${color.border.default}`, boxShadow: shadow.sm, borderRadius: radius.md, padding: 18, cursor: "pointer", textAlign: "left", width: "100%", color: "inherit", fontFamily: "inherit" },
  actionActive: { borderColor: color.accent.primary, boxShadow: `0 0 0 2px ${color.accent.primary}22` },
  actionDisabled: { opacity: .5, cursor: "not-allowed" },
  actionIcon:   { fontSize: 28, marginBottom: 10 },
  actionIconRow:{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 10 },
  // Inline messaging surface (expanded under the action cards).
  msgPanel:     { background: color.surface.card, border: `1px solid ${color.border.default}`, boxShadow: shadow.sm, borderRadius: radius.lg, overflow: "hidden", marginBottom: 20, display: "flex", flexDirection: "column" },
  msgHeader:    { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", borderBottom: `1px solid ${color.border.default}`, flexShrink: 0 },
  msgTitle:     { fontSize: font.size.heading, fontWeight: font.weight.bold, color: color.text.primary },
  msgClose:     { minWidth: 44, minHeight: 44, background: "transparent", border: "none", color: color.text.secondary, cursor: "pointer", fontSize: font.size.heading, lineHeight: 1 },
  msgBody:      { display: "flex", minHeight: 0, height: 460 },
  msgMaster:    { width: 240, flexShrink: 0, borderRight: `1px solid ${color.border.default}`, minHeight: 0, overflowY: "auto" },
  msgMasterNarrow: { width: "100%", borderRight: "none" },
  msgDetail:    { flex: 1, minWidth: 0, minHeight: 0 },
  actionTitle:  { fontSize: 15, fontWeight: font.weight.bold, color: color.text.primary },
  actionSub:    { fontSize: font.size.label, color: color.text.muted, marginTop: 4 },
  panel:        { background: color.surface.card, border: `1px solid ${color.border.default}`, boxShadow: shadow.sm, borderRadius: radius.md, padding: 16, marginBottom: 20 },
  panelTitle:   { fontSize: 14, fontWeight: font.weight.bold, marginBottom: 10, color: color.text.primary },
  outlineBtn:   { background: "transparent", border: `1px solid ${color.border.default}`, color: color.text.secondary, borderRadius: radius.sm, padding: "8px 16px", fontSize: 13, cursor: "pointer" },
  warningBtn:   { background: "#f97316", color: "#fff", border: "none", borderRadius: radius.sm, padding: "8px 16px", fontSize: 13, fontWeight: font.weight.bold, cursor: "pointer" },
  primaryBtn:   { background: color.accent.primary, color: "#fff", border: "none", borderRadius: radius.sm, padding: "8px 16px", fontSize: 13, fontWeight: font.weight.bold, cursor: "pointer" },
  overlay:      { position: "fixed", inset: 0, background: "rgba(0,0,0,.40)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200, padding: 16 },
  modal:        { background: color.surface.card, border: `1px solid ${color.border.default}`, boxShadow: shadow.lg, borderRadius: radius.lg, padding: 26, width: 420, maxWidth: "95vw" },
  modalTitle:   { fontSize: font.size.heading, fontWeight: font.weight.bold, marginBottom: 4, color: color.text.primary },
  modalSub:     { fontSize: 13, color: color.text.muted, marginBottom: 20 },
  formGroup:    { marginBottom: 16 },
  label:        { display: "block", fontSize: font.size.label, fontWeight: font.weight.bold, color: color.text.secondary, marginBottom: 6 },
  select:       { width: "100%", background: color.surface.raised, border: `1px solid ${color.border.default}`, color: color.text.primary, borderRadius: radius.sm, padding: "9px 12px", fontSize: 13 },
  input:        { width: "100%", background: color.surface.raised, border: `1px solid ${color.border.default}`, color: color.text.primary, borderRadius: radius.sm, padding: "9px 12px", fontSize: 13 },
  modalActions: { display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 20 },
};
